import type { ComponentType, SVGProps } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Calendar, Lock, MessageCircle, Route, Settings, Users } from 'lucide-react';
import { Card, EmptyState, PageBody, PracticeIcon, buttonVariants } from '@wundo/ui';
import { PageTitle } from '@/components/PageTitle.tsx';
import { m } from '@/paraglide/messages.js';

interface HeldBackSection {
  matches: (pathname: string) => boolean;
  title: () => string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

/* The same title and icon as the nav, so the held-back page still reads as the tab that was clicked. */
const SECTIONS: readonly HeldBackSection[] = [
  {
    matches: (pathname) => pathname === '/' || pathname.startsWith('/meetings'),
    title: m.nav_agenda,
    icon: Calendar,
  },
  {
    matches: (pathname) => pathname.startsWith('/conversations'),
    title: m.nav_conversations,
    icon: MessageCircle,
  },
  { matches: (pathname) => pathname.startsWith('/team'), title: m.nav_team, icon: Users },
  {
    matches: (pathname) => pathname.startsWith('/journeys'),
    title: m.nav_journeys,
    icon: Route,
  },
  {
    matches: (pathname) => pathname.startsWith('/practice'),
    title: m.nav_practice,
    icon: PracticeIcon,
  },
  {
    matches: (pathname) => pathname.startsWith('/settings'),
    title: m.settings_title,
    icon: Settings,
  },
];

/**
 * What a held-back page shows when a demo organisation reaches it by its
 * address. In the app itself those tabs are greyed out and only open a dialog.
 */
export function DemoUnavailablePage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const section = SECTIONS.find((candidate) => candidate.matches(pathname));
  const Icon = section?.icon ?? Lock;

  return (
    <>
      {section && <PageTitle>{section.title()}</PageTitle>}
      <PageBody
        fill
        className='flex items-center justify-center pb-10 max-mobile:basis-auto'
      >
        <Card className='w-full max-w-120 py-8'>
          <EmptyState
            icon={<Icon />}
            title={m.demo_unavailable_title()}
            description={m.demo_unavailable_description()}
            action={
              <Link to='/practice' className={buttonVariants()}>
                {m.demo_unavailable_practice()}
              </Link>
            }
          />
        </Card>
      </PageBody>
    </>
  );
}
